import { api, serverUrl } from "Requests/Api";
import { Logger } from "frontend-lib/Log";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "State/Global/AppContext";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { IonIcon } from "@ionic/react";
import { personOutline } from "ionicons/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AgentStates } from "frontend-lib/DomainTypes/User";

type Props = {
    isLoadingCb: (isLoading: boolean) => void;
};

export default function AgentDisplayName({ isLoadingCb }: Props) {
    //@ts-ignore
    const { actions, appState } = useAppContext();

    const logger = useMemo(() => new Logger("AgentState.tsx", serverUrl), []);
    const { t } = useTranslation();

    const agentState: { id: number; name: AgentStates } | undefined = useMemo(() => {
        return appState.agent.agentState;
    }, [appState.agent?.agentState]);

    const currentUserAgentId = useMemo(() => {
        return appState.agent.agentId;
    }, [appState.agent?.agentId]);

    const showAgentEmailAsAgentIdentifier = useMemo(() => {
        return Boolean(appState.workspaceConfig?.agent?.showAgentEmailAsAgentIdentifier);
    }, [appState.workspaceConfig?.agent?.showAgentEmailAsAgentIdentifier]);

    const agentEmail = useMemo(() => {
        return appState.agent?.email;
    }, [appState.agent?.email]);

    const displayName = useMemo(() => {
        if (showAgentEmailAsAgentIdentifier) {
            return agentEmail;
        }
        return appState.agent?.firstName;
    }, [appState, agentEmail, showAgentEmailAsAgentIdentifier]);

    const [agentNameColor, setAgentNameColor] = useState<{ name: string; color: string }>();

    useEffect(() => {
        let agentStateName = "";
        let agentIndicatorColor = "#ffffff";
        if (agentState?.name === "AWAY") {
            agentStateName = t("agentStateAway");
            agentIndicatorColor = "#ff8000";
        } else if (agentState?.name === "OFFLINE") {
            agentStateName = t("agentStateOffline");
            agentIndicatorColor = "#787777";
        } else {
            // Default case is online
            agentStateName = t("agentStateOnline");
            agentIndicatorColor = "#15ff00";
        }
        setAgentNameColor({ name: agentStateName, color: agentIndicatorColor });
    }, [agentState, t]);

    const handleAgentStateChange = async () => {
        let newAgentStateName = "ONLINE";

        if (agentState?.name === "AWAY") {
            newAgentStateName = "ONLINE";
        } else if (agentState?.name === "ONLINE") {
            newAgentStateName = "AWAY";
        }

        isLoadingCb(true);
        try {
            const res = await api.users().updateAgentState(currentUserAgentId, newAgentStateName);
            actions.setAgentState(res.data);
        } catch (err) {
            logger.error(
                `Error in handleAgentStateChange while calling API users().updateAgentState(). 
                Error was: ${err} json: ${JSON.stringify(err)}`
            );
        } finally {
            isLoadingCb(false);
        }
    };

    return (
        <p style={{ cursor: "pointer" }} onClick={handleAgentStateChange}>
            <IonIcon icon={personOutline} />
            {displayName} - {agentNameColor?.name}
            <FontAwesomeIcon
                style={{
                    color: agentNameColor?.color,
                    fontSize: "0.7rem",
                    marginTop: "6px",
                    marginLeft: "6px",
                }}
                /**
                @ts-ignore */
                icon={faCircle}
            />
        </p>
    );
}
